import type { ClaimWorkItem } from '../../../store';
import type { ClaimWorkRowAdditions, QueueViewKey } from '../../../types/encounterWork';
import { WORK_VIEW_LABELS, WORK_VIEW_RAIL_COLORS } from '../../../types/encounterWork';

/**
 * The five queues, in the order Home shows them (ADR-010, contract v1
 * section 1; Bill secondary since GDN-4023).
 *
 * Labels, captions, and colors are the PRODUCT's words and the rail's
 * existing values — copied from the contract, never invented here, so the
 * Home card, the Claims rail, and the workbench queue row all say the same
 * thing about the same queue. The caption under each count is the queue's
 * predicate in a biller's words; there is nothing else to say, and the copy
 * rule says not to say it.
 */
export interface HomeQueueDefinition {
  key: QueueViewKey;
  /** The queue's name, and the `queue.label` the workbench rail shows. */
  label: string;
  /** The rail tick color (contract section 1, existing values). */
  color: string;
  caption: string;
  /** Header for the one column this queue owns beyond Date, Patient, Payer. */
  detailColumnLabel: string;
}

/** Only the caption and the extra column are Home's to choose. */
const HOME_QUEUE_COPY: ReadonlyArray<{
  key: QueueViewKey;
  caption: string;
  detailColumnLabel: string;
}> = [
  {
    key: 'submit',
    caption: 'scrubbed, no blockers · oldest first',
    detailColumnLabel: 'Charge',
  },
  {
    key: 'review',
    caption: 'blockers or flags to clear before submitting',
    detailColumnLabel: 'Issues',
  },
  {
    key: 'awaiting_payer',
    caption: 'no payer activity for 30 days or more',
    detailColumnLabel: 'Silent for',
  },
  {
    key: 'fix',
    caption: 'rejected, denied, reversed, or a flagged partial',
    detailColumnLabel: 'Outcome',
  },
  {
    key: 'bill_secondary',
    caption: 'secondary drafts to send, or a primary whose draft could not be made',
    detailColumnLabel: 'Secondary',
  },
];

export const HOME_QUEUES: readonly HomeQueueDefinition[] = HOME_QUEUE_COPY.map(entry => ({
  ...entry,
  color: WORK_VIEW_RAIL_COLORS[entry.key],
  label: WORK_VIEW_LABELS[entry.key],
}));

/**
 * A claim work row as Home reads it.
 *
 * The section 5 additions are OPTIONAL here even though the contract makes
 * them required, because the deployed server may not carry them yet: the
 * generated client is packet F's, and a Home that crashed on an older API
 * would take the whole tab down. Every cell that needs one says what it says
 * when it is absent.
 *
 * `encounter_id` and `claim_number` carry their own nulls straight from
 * `ClaimWorkRowAdditions` (contract v1.2): a claim whose lines name no
 * encounter has none, and `patient_control_number` is null before
 * submission. Home excludes a null-encounter row from the queue's entries
 * and still shows it on the card; it renders no claim number in v0.
 */
export type HomeQueueRow = ClaimWorkItem & Partial<ClaimWorkRowAdditions>;

/** Rows Home asks for per queue. The count is the honest denominator. */
export const HOME_QUEUE_ROW_LIMIT = 5;
